import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Clock, User, Tag, MessageCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { BRAND } from "@/lib/data";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api.get(`/blog/posts/${slug}`)
      .then(({ data }) => {
        setPost(data);
        document.title = `${data.title} | Be Easy Cars Blog`;
        const desc = document.querySelector('meta[name="description"]');
        if (desc) desc.setAttribute("content", data.excerpt);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <div className="min-h-[60vh] flex items-center justify-center pt-24">
      <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
    </div>
  );

  if (notFound || !post) return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center px-6 pt-24 text-center" data-testid="blog-not-found">
      <h1 className="font-display text-4xl font-bold text-white mb-4">Article not found</h1>
      <Link to="/blog"><Button className="bg-red-600 hover:bg-red-700 rounded-full"><ArrowLeft className="w-4 h-4 mr-2" />Back to Blog</Button></Link>
    </div>
  );

  return (
    <article data-testid={`blog-post-${slug}`}>
      {/* Hero */}
      <section className="relative pt-32 pb-8 md:pt-40 md:pb-12 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="absolute -top-40 right-1/4 w-[600px] h-[500px] radial-glow-red pointer-events-none" />

        <div className="relative max-w-4xl mx-auto px-6 lg:px-10">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-8 transition-colors"
            data-testid="blog-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> All articles
          </Link>

          <div className="flex flex-wrap gap-2 mb-5">
            {post.tags.map((t) => (
              <span key={t} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-red-400 bg-red-500/10 border border-red-500/30 px-2.5 py-1 rounded-full">
                <Tag className="w-3 h-3" /> {t}
              </span>
            ))}
          </div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-display text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter text-white leading-[1.05]"
          >
            {post.title}
          </motion.h1>

          <div className="mt-6 flex items-center gap-6 text-sm text-slate-400 font-mono-brand text-xs uppercase tracking-widest">
            <span className="flex items-center gap-1.5"><User className="w-3 h-3" /> {post.author}</span>
            <span className="flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {post.published_at}</span>
            <span className="flex items-center gap-1.5"><Clock className="w-3 h-3" /> {post.read_minutes} min read</span>
          </div>
        </div>
      </section>

      {/* Cover */}
      <div className="max-w-5xl mx-auto px-6 lg:px-10 mb-12">
        <div className="rounded-3xl overflow-hidden aspect-[16/9] border border-white/10">
          <img src={post.cover} alt={post.title} className="w-full h-full object-cover" />
        </div>
      </div>

      {/* Content */}
      <section className="pb-12">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 space-y-5">
          <p className="text-lg md:text-xl text-slate-300 leading-relaxed font-medium">{post.excerpt}</p>
          {post.content.map((block, i) => {
            if (block.h) {
              return <h2 key={i} className="font-display text-2xl md:text-3xl font-bold text-white mt-10 mb-3">{block.h}</h2>;
            }
            if (block.p) {
              return <p key={i} className="text-slate-300 leading-relaxed text-base md:text-lg">{block.p}</p>;
            }
            if (block.ul) {
              return (
                <ul key={i} className="space-y-2 pl-2">
                  {block.ul.map((item, k) => (
                    <li key={k} className="flex items-start gap-3 text-slate-300 text-base md:text-lg">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-2.5 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              );
            }
            return null;
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <div className="relative overflow-hidden rounded-3xl glass p-8 md:p-10">
            <div className="absolute -top-16 -right-16 w-64 h-64 radial-glow-red pointer-events-none" />
            <div className="relative">
              <h3 className="font-display text-2xl md:text-3xl font-bold text-white">
                Ready to start your taxi business?
              </h3>
              <p className="mt-3 text-slate-400">Talk to a Be Easy Cars expert. Vehicle, finance and permit — sorted.</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/contact"><Button className="bg-red-600 hover:bg-red-700 rounded-full">Free Consultation</Button></Link>
                <a href={BRAND.whatsappUrl} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="border-green-500/40 bg-green-500/10 text-green-400 hover:bg-green-500/20 rounded-full">
                    <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </article>
  );
}
